import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Upload, AlertTriangle, CheckCircle, Database,
  Clock, Loader2, FileJson, Shield, Trash2, RefreshCw,
  HardDrive, CalendarClock, Server, Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}

export default function AdminBackup() {
  const [logs, setLogs] = useState([]);
  const [localBackups, setLocalBackups] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [stats, setStats] = useState({ total: 0, total_size_mb: 0, retention_days: 30 });

  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/admin/system/backup/list`, { withCredentials: true });
      setLogs(res.data.logs || []);
    } catch {
      toast.error("Gagal memuat riwayat backup");
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  const loadLocalBackups = useCallback(async () => {
    setLoadingLocal(true);
    try {
      const res = await axios.get(`${API}/admin/system/backups`, { withCredentials: true });
      setLocalBackups(res.data.backups || []);
      setStats({
        total: res.data.total || 0,
        total_size_mb: res.data.total_size_mb || 0,
        retention_days: res.data.retention_days || 30,
      });
    } catch {
      toast.error("Gagal memuat daftar backup lokal");
    } finally {
      setLoadingLocal(false);
    }
  }, []);

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/system/backup/scheduler-status`, { withCredentials: true });
      setScheduler(res.data);
    } catch {
      setScheduler(null);
    }
  }, []);

  useEffect(() => {
    loadLogs();
    loadLocalBackups();
    loadSchedulerStatus();
  }, [loadLogs, loadLocalBackups, loadSchedulerStatus]);

  const handleExportBackup = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/system/backup`, {
        withCredentials: true,
        responseType: "blob",
      });
      const collections = res.headers["x-backup-collections"];
      const documents = res.headers["x-backup-documents"];
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `seikat_backup_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Backup di-unduh: ${collections} koleksi, ${documents} dokumen`);
    } catch (err) {
      toast.error("Gagal export: " + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleSaveToServer = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/system/backup/save`, {}, { withCredentials: true });
      const b = res.data?.backup || {};
      toast.success(`Backup tersimpan di server: ${b.filename} (${b.size_mb} MB, ${b.total_documents} dokumen)`);
      await loadLocalBackups();
      await loadLogs();
    } catch (err) {
      toast.error("Gagal simpan ke server: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadLocal = async (filename) => {
    try {
      const res = await axios.get(`${API}/admin/system/backups/${filename}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Diunduh: ${filename}`);
    } catch (err) {
      toast.error("Gagal mengunduh: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteLocal = async (filename) => {
    if (!window.confirm(`Hapus file backup ini?\n\n${filename}\n\nTindakan ini tidak dapat dibatalkan.`)) return;
    setDeleting(filename);
    try {
      await axios.delete(`${API}/admin/system/backups/${filename}`, { withCredentials: true });
      toast.success(`Backup dihapus: ${filename}`);
      await loadLocalBackups();
    } catch (err) {
      toast.error("Gagal menghapus: " + (err.response?.data?.detail || err.message));
    } finally {
      setDeleting(null);
    }
  };

  const handleRunCleanup = async () => {
    if (!window.confirm(`Jalankan pembersihan backup lebih dari ${stats.retention_days} hari?`)) return;
    try {
      const res = await axios.post(`${API}/admin/system/backup/cleanup`, {}, { withCredentials: true });
      const r = res.data?.result || {};
      toast.success(`Cleanup selesai: ${r.deleted_count || 0} file dihapus, ${r.kept_count || 0} disimpan`);
      await loadLocalBackups();
    } catch (err) {
      toast.error("Gagal cleanup: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleTriggerScheduled = async () => {
    if (!window.confirm("Jalankan backup terjadwal secara manual sekarang?")) return;
    try {
      await axios.post(`${API}/admin/system/backup/run-scheduled`, {}, { withCredentials: true });
      toast.success("Backup terjadwal dijalankan");
      await loadLocalBackups();
      await loadLogs();
    } catch (err) {
      toast.error("Gagal trigger: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith(".json")) {
        toast.error("File harus berformat .json");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) {
      toast.error("Pilih file backup terlebih dahulu");
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const formData = new FormData();
      formData.append("backup_file", selectedFile);
      formData.append("restore_mode", restoreMode);
      const res = await axios.post(`${API}/admin/system/restore`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreResult(res.data);
      toast.success(`Restore berhasil: ${(res.data.restore_stats?.documents_inserted || 0) + (res.data.restore_stats?.documents_updated || 0)} dokumen`);
      await loadLogs();
      setTimeout(() => {
        setShowRestoreModal(false);
        setSelectedFile(null);
        setRestoreResult(null);
      }, 5000);
    } catch (err) {
      toast.error("Gagal restore: " + (err.response?.data?.detail || err.message));
      setRestoreResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-2 py-2" data-testid="admin-backup-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl text-[#1C1A19] mb-2 flex items-center gap-2">
          <Database size={28} className="text-seikat-pink" />
          System Backup & Restore
        </h1>
        <p className="font-body text-sm text-[#8A857D]">
          Kelola backup penuh sistem, jadwal otomatis, dan disaster recovery
        </p>
      </div>

      {/* Scheduler Status Banner */}
      {scheduler && (
        <div className="mb-6 bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-5" data-testid="scheduler-status-banner">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${scheduler.enabled ? "bg-emerald-50" : "bg-gray-100"}`}>
                <CalendarClock size={20} className={scheduler.enabled ? "text-emerald-600" : "text-gray-400"} />
              </div>
              <div>
                <p className="font-body text-sm font-medium text-[#1C1A19]">
                  Auto-Backup {scheduler.enabled ? "Aktif" : "Nonaktif"}
                </p>
                <p className="font-body text-xs text-[#8A857D]">
                  Mingguan ({scheduler.day_of_week?.toUpperCase()}) jam {String(scheduler.hour_utc).padStart(2, "0")}:00 UTC •
                  Retensi {scheduler.retention_days} hari
                </p>
              </div>
            </div>
            <div className="flex flex-col md:items-end">
              <span className="font-body text-[10px] tracking-widest uppercase text-[#A8A29A]">Jadwal Berikutnya</span>
              <span className="font-body text-sm text-seikat-pink font-medium" data-testid="next-run-at">
                {scheduler.next_run_at ? formatDate(scheduler.next_run_at) : "-"}
              </span>
            </div>
            <button
              onClick={handleTriggerScheduled}
              data-testid="trigger-scheduled-btn"
              className="px-4 py-2 rounded-xl bg-seikat-pink/10 text-seikat-pink hover:bg-seikat-pink hover:text-white text-sm font-body transition-colors"
            >
              Jalankan Sekarang
            </button>
          </div>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {/* Save to Server Card */}
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-seikat-pink/10 flex items-center justify-center">
              <Save size={22} className="text-seikat-pink" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#1C1A19] mb-1">Simpan ke Server</h3>
              <p className="font-body text-xs text-[#8A857D]">
                Buat backup baru dan simpan di folder server <code className="text-[10px] bg-[#FDF8F6] px-1 rounded">/app/backups/</code>
              </p>
            </div>
          </div>
          <button
            onClick={handleSaveToServer}
            disabled={saving}
            data-testid="save-backup-server-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (<><Loader2 size={16} className="animate-spin" /> Menyimpan...</>) : (<><Save size={16} /> Buat Backup Baru</>)}
          </button>
        </div>

        {/* Download Backup Card */}
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Download size={22} className="text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#1C1A19] mb-1">Unduh Langsung</h3>
              <p className="font-body text-xs text-[#8A857D]">
                Buat dan unduh file backup JSON langsung ke komputer Anda
              </p>
            </div>
          </div>
          <button
            onClick={handleExportBackup}
            disabled={exporting}
            data-testid="export-backup-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-body font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (<><Loader2 size={16} className="animate-spin" /> Mengunduh...</>) : (<><Download size={16} /> Unduh Backup</>)}
          </button>
        </div>

        {/* Restore Card */}
        <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center">
              <Upload size={22} className="text-rose-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#1C1A19] mb-1">Restore</h3>
              <p className="font-body text-xs text-[#8A857D]">
                Pulihkan data dari file backup JSON (merge atau replace)
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowRestoreModal(true)}
            data-testid="open-restore-modal-btn"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-body font-medium hover:bg-rose-700 transition-colors"
          >
            <Upload size={16} /> Import & Restore
          </button>
        </div>
      </div>

      {/* Local Backups */}
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6 mb-8" data-testid="local-backups-section">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h3 className="font-heading text-lg text-[#1C1A19] flex items-center gap-2">
              <HardDrive size={20} className="text-[#8A857D]" />
              Backup Tersimpan di Server
            </h3>
            <p className="font-body text-xs text-[#8A857D] mt-1">
              {stats.total} file • Total {stats.total_size_mb} MB • Retensi {stats.retention_days} hari
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRunCleanup}
              data-testid="run-cleanup-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-body text-[#8A857D] hover:bg-[#FDF8F6] transition-colors border border-[#D4859B]/12"
            >
              <Trash2 size={14} /> Bersihkan yang Lama
            </button>
            <button
              onClick={loadLocalBackups}
              data-testid="refresh-local-backups-btn"
              className="w-9 h-9 rounded-lg text-[#8A857D] hover:bg-[#FDF8F6] flex items-center justify-center transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {loadingLocal ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-seikat-pink" size={24} />
          </div>
        ) : localBackups.length === 0 ? (
          <div className="text-center py-12">
            <Server size={32} className="mx-auto text-[#D4859B]/30 mb-3" />
            <p className="font-body text-sm text-[#8A857D]">Belum ada backup tersimpan di server</p>
            <p className="font-body text-xs text-[#A8A29A] mt-1">Klik &quot;Buat Backup Baru&quot; untuk memulai</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="local-backups-table">
              <thead>
                <tr className="text-left text-[10px] tracking-widest uppercase text-[#A8A29A] border-b border-[#D4859B]/12">
                  <th className="py-3 pr-3 font-body">Nama File</th>
                  <th className="py-3 px-3 font-body">Ukuran</th>
                  <th className="py-3 px-3 font-body">Dibuat</th>
                  <th className="py-3 px-3 font-body">Usia</th>
                  <th className="py-3 pl-3 text-right font-body">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {localBackups.map((b) => (
                  <tr key={b.filename} className="border-b border-[#D4859B]/8 hover:bg-[#FDF8F6]/40 transition-colors">
                    <td className="py-3 pr-3 font-body text-sm text-[#1C1A19]" data-testid={`backup-row-${b.filename}`}>
                      <div className="flex items-center gap-2">
                        <FileJson size={14} className="text-seikat-pink flex-shrink-0" />
                        <span className="truncate">{b.filename}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 font-body text-xs text-[#4A453F]">{formatBytes(b.size_bytes)}</td>
                    <td className="py-3 px-3 font-body text-xs text-[#4A453F]">{formatDate(b.created_at)}</td>
                    <td className="py-3 px-3 font-body text-xs text-[#4A453F]">
                      <span className={`px-2 py-0.5 rounded ${b.age_days > stats.retention_days ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {b.age_days} hari
                      </span>
                    </td>
                    <td className="py-3 pl-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDownloadLocal(b.filename)}
                          data-testid={`download-${b.filename}`}
                          className="px-2.5 py-1.5 rounded-lg text-xs text-emerald-700 hover:bg-emerald-50 transition-colors flex items-center gap-1"
                          aria-label="Download"
                        >
                          <Download size={13} /> Unduh
                        </button>
                        <button
                          onClick={() => handleDeleteLocal(b.filename)}
                          disabled={deleting === b.filename || localBackups.length <= 1}
                          data-testid={`delete-${b.filename}`}
                          className="px-2.5 py-1.5 rounded-lg text-xs text-rose-700 hover:bg-rose-50 transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={localBackups.length <= 1 ? "Tidak dapat menghapus backup terakhir" : "Hapus backup"}
                          aria-label="Delete"
                        >
                          {deleting === b.filename ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          Hapus
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Activity History */}
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#1C1A19] flex items-center gap-2">
            <Clock size={20} className="text-[#8A857D]" />
            Riwayat Aktivitas
          </h3>
          <button
            onClick={loadLogs}
            data-testid="refresh-logs-btn"
            className="text-[#8A857D] hover:text-seikat-pink"
            aria-label="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {loadingLogs ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-seikat-pink" size={24} />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-center text-[#A8A29A] text-sm py-10">Belum ada riwayat backup/restore</p>
        ) : (
          <div className="space-y-2">
            {logs.map((log, idx) => {
              const isRestore = log.action === "system_restore";
              return (
                <div key={idx} className="flex items-start gap-3 p-3 bg-[#FDF8F6] rounded-xl">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isRestore ? "bg-blue-100" : "bg-emerald-100"}`}>
                    {isRestore ? <Upload size={14} className="text-blue-600" /> : <Download size={14} className="text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-[#1C1A19] font-medium">
                      {isRestore ? "System Restore" : "Backup Export"}
                    </p>
                    <p className="font-body text-xs text-[#8A857D]">
                      {formatDate(log.created_at)} • Oleh: {log.performed_by_email || log.performed_by || "System"}
                    </p>
                    {log.restore_stats && (
                      <p className="font-body text-xs text-[#8A857D] mt-1">
                        {log.restore_stats.documents_inserted} disisipkan, {log.restore_stats.documents_updated} diperbarui
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="restore-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#D4859B]/12 px-6 py-4 flex items-center justify-between">
              <h3 className="font-heading text-xl text-[#1C1A19]">Import &amp; Restore Backup</h3>
              <button
                onClick={() => { setShowRestoreModal(false); setSelectedFile(null); setRestoreResult(null); }}
                data-testid="close-restore-modal"
                className="w-8 h-8 rounded-full hover:bg-[#D4859B]/10 flex items-center justify-center"
                aria-label="Close"
              >
                <span className="text-[#8A857D]">×</span>
              </button>
            </div>

            <div className="px-6 py-6 space-y-5">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-3">
                  <Shield size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-body text-sm font-medium text-amber-900 mb-1">Penting: Backup Data Saat Ini</p>
                    <p className="font-body text-xs text-amber-800">
                      Sangat disarankan untuk membuat backup data saat ini sebelum restore. Restore dapat menimpa data yang ada.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-2">Pilih File Backup (.json)</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  disabled={restoring}
                  data-testid="backup-file-input"
                  className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50 disabled:opacity-50"
                />
                {selectedFile && (
                  <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1">
                    <FileJson size={14} />
                    {selectedFile.name} ({formatBytes(selectedFile.size)})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-2">Mode Restore</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-[#D4859B]/20 rounded-xl cursor-pointer hover:bg-[#FDF8F6] transition-colors">
                    <input
                      type="radio"
                      name="restore_mode"
                      value="merge"
                      checked={restoreMode === "merge"}
                      onChange={(e) => setRestoreMode(e.target.value)}
                      disabled={restoring}
                      className="mt-1"
                      data-testid="restore-mode-merge"
                    />
                    <div>
                      <p className="font-body text-sm font-medium text-[#1C1A19]">Merge (Gabung — Direkomendasikan)</p>
                      <p className="font-body text-xs text-[#8A857D]">
                        Perbarui data yang ada dan tambahkan yang baru. Data lama tidak dihapus.
                      </p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-3 border-2 border-rose-200 rounded-xl cursor-pointer hover:bg-rose-50 transition-colors">
                    <input
                      type="radio"
                      name="restore_mode"
                      value="replace"
                      checked={restoreMode === "replace"}
                      onChange={(e) => setRestoreMode(e.target.value)}
                      disabled={restoring}
                      className="mt-1"
                      data-testid="restore-mode-replace"
                    />
                    <div>
                      <p className="font-body text-sm font-medium text-rose-900 flex items-center gap-2">
                        Replace (Ganti Total)
                        <span className="px-1.5 py-0.5 bg-rose-200 text-rose-800 text-[10px] rounded">BERBAHAYA</span>
                      </p>
                      <p className="font-body text-xs text-rose-800">
                        Menghapus SEMUA data lama dan menggantinya dengan backup. Gunakan dengan hati-hati!
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {restoreResult && (
                <div className={`rounded-xl p-4 ${restoreResult.success ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                  <div className="flex items-start gap-2">
                    {restoreResult.success ? (
                      <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" />
                    ) : (
                      <AlertTriangle size={20} className="text-rose-600 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`font-body text-sm font-medium mb-2 ${restoreResult.success ? "text-emerald-900" : "text-rose-900"}`}>
                        {restoreResult.success ? "Restore Berhasil" : "Restore Gagal"}
                      </p>
                      {restoreResult.success && restoreResult.restore_stats && (
                        <div className="font-body text-xs text-emerald-800 space-y-1">
                          <p>Collections: {restoreResult.restore_stats.collections_processed}</p>
                          <p>Disisipkan: {restoreResult.restore_stats.documents_inserted}</p>
                          <p>Diperbarui: {restoreResult.restore_stats.documents_updated}</p>
                          {restoreResult.restore_stats.documents_skipped > 0 && (
                            <p>Dilewati: {restoreResult.restore_stats.documents_skipped}</p>
                          )}
                        </div>
                      )}
                      {restoreResult.error && (
                        <p className="font-body text-xs text-rose-800">{restoreResult.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-[#D4859B]/12">
                <button
                  onClick={() => { setShowRestoreModal(false); setSelectedFile(null); setRestoreResult(null); }}
                  disabled={restoring}
                  data-testid="cancel-restore-btn"
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-body text-[#8A857D] hover:bg-[#FDF8F6] transition-colors disabled:opacity-50"
                >
                  Batal
                </button>
                <button
                  onClick={handleRestore}
                  disabled={!selectedFile || restoring}
                  data-testid="confirm-restore-btn"
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-body font-medium bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {restoring ? (<><Loader2 size={14} className="animate-spin" /> Memulihkan...</>) : (<><Upload size={14} /> Mulai Restore</>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
